import { useLayoutEffect, useRef } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomInputField from "../../../../CustomHTMLElements/CustomInputField";
import CustomSelectDropdown from "../../../../CustomHTMLElements/CustomSelectDropdown";
import styles from "./BvnVerifiedDetails.module.scss";
import HeaderTitle from "../../Components/HeaderTitle";

const schema = yup.object().shape({
  first_name: yup
    .string()
    .required("Please enter first name")
    .min(1, "First name is required")
    .max(30, "First name must not exceed 30 characters"),
  last_name: yup
    .string()
    .required("Please enter last name")
    .min(1, "Last name is required")
    .max(30, "Last name must not exceed 30 characters"),
  gender: yup.string().required("Please choose gender"),
  dob: yup.string().required("Please enter Date Of Birth"),
  // Country is a fixed disabled field, no validation needed
  country: yup.string(),
});

type FormData = {
  first_name: string;
  last_name: string;
  gender: string;
  dob: string;
  country: string;
};

interface BvnVerifiedDetailsProps {
  onContinue: (data: FormData) => void;
  defaultValues?: Partial<FormData>;
}

const BvnVerifiedDetails: React.FC<BvnVerifiedDetailsProps> = ({
  onContinue,
  defaultValues = {},
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  const { register, handleSubmit, errors, setValue } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: defaultValues.first_name || "",
      last_name: defaultValues.last_name || "",
      gender: defaultValues.gender || "",
      dob: defaultValues.dob || "",
      country: defaultValues.country || "NG",
    },
  });

  useLayoutEffect(() => {
    setValue("first_name", defaultValues.first_name || "");
    setValue("last_name", defaultValues.last_name || "");
    setValue("gender", defaultValues.gender || "");
    setValue("dob", defaultValues.dob || "");
    setValue("country", defaultValues.country || "NG", {
      shouldValidate: true,
    });
  }, [defaultValues]);

  const onSubmit = (formData: FormData) => {
    // Ensure country is always set since the field is disabled
    onContinue({
      ...formData,
      country: formData.country || "NG",
    });
  };

  return (
    <div className={styles.detailsContainer}>
      <div className={styles.content}>
        <HeaderTitle>Personal Information</HeaderTitle>
        <p className={styles.subtitle}>
          Kindly review the pre-filled information below
        </p>

        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <div className="row mt-4">
            <div className="col-12">
              <CustomInputField
                type="text"
                name="first_name"
                reference={register}
                placeholder="John"
                label="First Name"
                errors={errors?.first_name}
                maxLength={30}
                readOnly={true}
                value={defaultValues.first_name || ""}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                name="last_name"
                reference={register}
                placeholder="Doe"
                label="Last Name"
                errors={errors?.last_name}
                maxLength={30}
                readOnly={true}
                value={defaultValues.last_name || ""}
              />
            </div>

            <div className="col-12 mt-n3 mb-1">
              <CustomSelectDropdown
                name="country"
                reference={register}
                label="Country"
                errors={errors?.country}
                disabled={true}
                isShowLabel={true}
                defaultValue="NG"
              >
                <option value="NG">Nigeria</option>
              </CustomSelectDropdown>
            </div>

            <div className="col-12">
              <CustomInputField
                type="text"
                name="gender"
                reference={register}
                placeholder="Gender"
                label="Gender"
                errors={errors?.gender}
                readOnly={true}
                value={defaultValues.gender || ""}
              />
            </div>

            <div className="col-12">
              <CustomInputField
                type="date"
                name="dob"
                reference={register}
                label="Date Of Birth"
                errors={errors?.dob}
                readOnly={true}
                value={defaultValues.dob || ""}
              />
            </div>
          </div>

          <div className={styles.actions}>
            <button type="submit" className="btn advancly-btn btn-lg btn-block">
              Proceed
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BvnVerifiedDetails;
